import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  WifiHigh,
  Plus,
  PencilSimple,
  Trash,
  Lock,
  Globe,
  TreeStructure,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";

const SECURITY_TYPES = [
  { value: "WPA2", label: "WPA2-Personal" },
  { value: "WPA3", label: "WPA3-Personal" },
  { value: "WPA2-Enterprise", label: "WPA2-Enterprise" },
  { value: "WPA3-Enterprise", label: "WPA3-Enterprise" },
  { value: "Open", label: "Open (No Password)" },
];

const emptyForm = {
  name: "",
  ssid: "",
  password: "",
  security_type: "WPA2",
  ip_range: "",
  subnet_mask: "255.255.255.0",
  gateway: "",
  dns_primary: "",
  dns_secondary: "",
  vlan_id: "",
  purpose: "",
  notes: "",
};

const NetworkPage = () => {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => { fetchNetworks(); }, []);

  const fetchNetworks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/network`);
      setNetworks(res.data);
    } catch (err) {
      toast.error("Failed to load networks");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.ssid.trim()) { toast.error("Name and SSID are required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/network/${editing.id}`, form);
        toast.success("Network updated");
      } else {
        await axios.post(`${API}/network`, form);
        toast.success("Network added");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(emptyForm);
      fetchNetworks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (net) => {
    setEditing(net);
    setForm({
      name: net.name || "",
      ssid: net.ssid || "",
      password: net.password || "",
      security_type: net.security_type || "WPA2",
      ip_range: net.ip_range || "",
      subnet_mask: net.subnet_mask || "255.255.255.0",
      gateway: net.gateway || "",
      dns_primary: net.dns_primary || "",
      dns_secondary: net.dns_secondary || "",
      vlan_id: net.vlan_id || "",
      purpose: net.purpose || "",
      notes: net.notes || "",
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/network/${id}`);
      toast.success("Network deleted");
      fetchNetworks();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const togglePassword = (id) => {
    setShowPasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="network-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Network</h1>
            <p className="text-muted-foreground">
              Configure WiFi networks for POS device communication
            </p>
          </div>
          <Button className="gap-2" onClick={handleAdd} data-testid="add-network-btn">
            <Plus size={18} /> Add Network
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <WifiHigh size={24} className="text-primary" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Networks</p>
                <p className="font-mono text-2xl font-medium" data-testid="network-total">{networks.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Lock size={24} className="text-green-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Secured</p>
                <p className="font-mono text-2xl font-medium">{networks.filter((n) => n.security_type !== "Open").length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <TreeStructure size={24} className="text-blue-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">VLANs</p>
                <p className="font-mono text-2xl font-medium">{networks.filter((n) => n.vlan_id).length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Network List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : networks.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <WifiHigh size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No networks configured</p>
              <p className="text-sm">Add a WiFi network for your POS devices</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {networks.map((net) => (
              <Card key={net.id} className="hover:shadow-sm transition-shadow" data-testid={`network-${net.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <WifiHigh size={20} className="text-primary" weight="fill" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{net.name}</CardTitle>
                        <CardDescription className="font-mono text-xs">{net.ssid}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(net)} data-testid={`edit-network-${net.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(net.id)} data-testid={`delete-network-${net.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs">Security</span>
                      <p className="font-medium flex items-center gap-1">
                        {net.security_type !== "Open" && <Lock size={12} weight="bold" />}
                        {net.security_type}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Password</span>
                      <div className="flex items-center gap-1">
                        <p className="font-mono text-xs">
                          {net.security_type === "Open" ? (
                            <span className="italic text-muted-foreground">None</span>
                          ) : showPasswords[net.id] ? (
                            net.password || "—"
                          ) : (
                            "********"
                          )}
                        </p>
                        {net.security_type !== "Open" && (
                          <button onClick={() => togglePassword(net.id)} className="text-muted-foreground hover:text-foreground p-0.5" data-testid={`toggle-pass-${net.id}`}>
                            {showPasswords[net.id] ? <EyeSlash size={14} /> : <Eye size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                    {net.ip_range && (
                      <div>
                        <span className="text-muted-foreground text-xs">IP Range</span>
                        <p className="font-mono text-xs">{net.ip_range}</p>
                      </div>
                    )}
                    {net.gateway && (
                      <div>
                        <span className="text-muted-foreground text-xs">Gateway</span>
                        <p className="font-mono text-xs">{net.gateway}</p>
                      </div>
                    )}
                    {net.dns_primary && (
                      <div>
                        <span className="text-muted-foreground text-xs">DNS</span>
                        <p className="font-mono text-xs">{net.dns_primary}{net.dns_secondary ? `, ${net.dns_secondary}` : ""}</p>
                      </div>
                    )}
                    {net.vlan_id && (
                      <div>
                        <span className="text-muted-foreground text-xs">VLAN</span>
                        <p className="font-mono text-xs">{net.vlan_id}</p>
                      </div>
                    )}
                  </div>
                  {net.purpose && (
                    <div className="mt-3 pt-2 border-t border-border">
                      <span className="text-muted-foreground text-xs">Purpose</span>
                      <p className="text-sm">{net.purpose}</p>
                    </div>
                  )}
                  {net.notes && (
                    <p className="text-xs text-muted-foreground mt-2 italic">{net.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); setEditing(null); } }}>
          <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto" data-testid="network-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editing ? "Edit Network" : "Add Network"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Network Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. POS Main Network" data-testid="network-name" />
                </div>
                <div className="space-y-2">
                  <Label>SSID</Label>
                  <Input value={form.ssid} onChange={(e) => setForm({ ...form, ssid: e.target.value })} placeholder="e.g. Restaurant-POS" data-testid="network-ssid" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Security Type</Label>
                  <Select value={form.security_type} onValueChange={(v) => setForm({ ...form, security_type: v })}>
                    <SelectTrigger data-testid="network-security"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SECURITY_TYPES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="WiFi password" data-testid="network-password" />
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-sm font-medium mb-3">IP Configuration</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IP Range / DHCP</Label>
                    <Input value={form.ip_range} onChange={(e) => setForm({ ...form, ip_range: e.target.value })} placeholder="e.g. 192.168.1.0/24" data-testid="network-ip-range" />
                  </div>
                  <div className="space-y-2">
                    <Label>Subnet Mask</Label>
                    <Input value={form.subnet_mask} onChange={(e) => setForm({ ...form, subnet_mask: e.target.value })} placeholder="255.255.255.0" data-testid="network-subnet" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label>Gateway</Label>
                    <Input value={form.gateway} onChange={(e) => setForm({ ...form, gateway: e.target.value })} placeholder="e.g. 192.168.1.1" data-testid="network-gateway" />
                  </div>
                  <div className="space-y-2">
                    <Label>VLAN ID</Label>
                    <Input value={form.vlan_id} onChange={(e) => setForm({ ...form, vlan_id: e.target.value })} placeholder="e.g. 10" data-testid="network-vlan" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label>Primary DNS</Label>
                    <Input value={form.dns_primary} onChange={(e) => setForm({ ...form, dns_primary: e.target.value })} placeholder="e.g. 8.8.8.8" data-testid="network-dns1" />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary DNS</Label>
                    <Input value={form.dns_secondary} onChange={(e) => setForm({ ...form, dns_secondary: e.target.value })} placeholder="e.g. 8.8.4.4" data-testid="network-dns2" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Purpose</Label>
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. POS terminals, Kitchen displays, Guest WiFi" data-testid="network-purpose" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={2} data-testid="network-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-network-btn">{editing ? "Save Changes" : "Add Network"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default NetworkPage;
